import { useProjectsControllerList, type SpaceDtoOutput } from '@batiflow/api-client';
import { FormField, UpgradePrompt } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PICKER_SIZE } from '@/features/projects/model';
import { useNotify } from '@/shared/hooks/use-notify';
import { useCreateSpace } from './api';
import { isPlanLimit, spaceErrorMessage } from './model';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"}});

type Kind = 'shared' | 'group';
const SHARED: Kind = 'shared';
const GROUP: Kind = 'group';
const isKind = (value: string): value is Kind => value === SHARED || value === GROUP;

export interface NewSpaceDialogProps {
  /** The organisation that hosts the new space (the active one). */
  orgId: string;
  open: boolean;
  /** A working group is for the owners and administrators; a project's space for its managers. */
  canCreateGroup: boolean;
  onClose: () => void;
  onCreated: (space: SpaceDtoOutput) => void;
}

type Problem = { kind: 'planLimit' } | { kind: 'other'; message: string };

function NewSpaceForm({
  orgId,
  canCreateGroup,
  onClose,
  onCreated,
}: Omit<NewSpaceDialogProps, 'open'>) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const create = useCreateSpace();
  // The projects the person manages: a project's shared space is linked to one of them.
  const projects = useProjectsControllerList(orgId, { limit: PICKER_SIZE });
  const managed = (projects.data?.items ?? []).filter((project) =>
    project._permissions.includes('can_manage'),
  );

  const [kind, setKind] = useState<Kind>(SHARED);
  const [projectId, setProjectId] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending) return;
    setProblem(null);
    const trimmed = name.trim();
    let invalid = false;
    if (trimmed.length === 0) {
      setNameError(t('create.errors.nameRequired'));
      invalid = true;
    } else if (trimmed.length > 200) {
      setNameError(t('create.errors.nameTooLong'));
      invalid = true;
    }
    if (kind === SHARED && projectId === '') {
      setProjectError(t('create.errors.projectRequired'));
      invalid = true;
    }
    if (invalid) return;
    try {
      const space = await create.mutateAsync({
        orgId,
        data: { kind, name: trimmed, ...(kind === SHARED ? { projectId } : {}) },
      });
      notify({ intent: 'success', message: t('create.created', { name: trimmed }) });
      onClose();
      onCreated(space);
    } catch (error) {
      setProblem(
        isPlanLimit(error)
          ? { kind: 'planLimit' }
          : { kind: 'other', message: spaceErrorMessage(t, tCommon, error, 'create') },
      );
    }
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <DialogBody>
        <DialogTitle>{t('create.title')}</DialogTitle>
        <DialogContent>
          <div className={styles.form}>
            <p className={styles.intro}>{t('create.intro')}</p>
            <FormField label={t('create.kind.label')} hint={t(`create.kind.${kind}.hint`)}>
              <RadioGroup
                value={kind}
                onChange={(_event, data) => {
                  if (isKind(data.value)) {
                    setKind(data.value);
                    setProblem(null);
                  }
                }}
              >
                <Radio value={SHARED} label={t('create.kind.shared.label')} />
                {canCreateGroup ? (
                  <Radio value={GROUP} label={t('create.kind.group.label')} />
                ) : null}
              </RadioGroup>
            </FormField>
            {kind === SHARED ? (
              managed.length === 0 && projects.isSuccess ? (
                <MessageBar intent="info" layout="multiline">
                  <MessageBarBody>{t('create.project.none')}</MessageBarBody>
                </MessageBar>
              ) : (
                <FormField required label={t('create.project.label')} error={projectError}>
                  <Select
                    value={projectId}
                    onChange={(_event, data) => {
                      setProjectId(data.value);
                      setProjectError(null);
                    }}
                  >
                    <option value="">{t('create.project.placeholder')}</option>
                    {managed.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.code} — {project.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
              )
            ) : null}
            <FormField required label={t('create.name.label')} error={nameError}>
              <Input
                autoComplete="off"
                value={name}
                onChange={(_event, data) => {
                  setName(data.value);
                  setNameError(null);
                }}
              />
            </FormField>
            {problem?.kind === 'planLimit' ? (
              // There is no plans page yet: the prompt explains and has no action button.
              <UpgradePrompt message={t('create.errors.planLimit')} />
            ) : null}
            {problem?.kind === 'other' ? (
              <MessageBar intent="error" layout="multiline">
                <MessageBarBody>{problem.message}</MessageBarBody>
              </MessageBar>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" appearance="secondary" onClick={onClose}>
            {tCommon('actions.cancel')}
          </Button>
          <Button type="submit" appearance="primary" disabledFocusable={create.isPending}>
            {create.isPending ? t('create.submitting') : t('create.submit')}
          </Button>
        </DialogActions>
      </DialogBody>
    </form>
  );
}

/**
 * "Nouvel espace partagé": a project's shared space (linked to a project the person manages) or a
 * working group (owners and administrators). A full plan is explained with the upgrade prompt; a
 * refusal in plain language.
 */
export function NewSpaceDialog({ open, onClose, ...rest }: NewSpaceDialogProps) {
  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <NewSpaceForm {...rest} onClose={onClose} />
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/new-space-dialog.griffel.css";